/**
 * Filtros de la pantalla de trazabilidad. Un solo campo de texto con selector de modo;
 * valida el formato del correo antes de dejar que el contenedor llame al backend.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type TraceFilterMode = 'subject' | 'email';

// Los segmentos del dominio excluyen el punto de su propia clase de caracteres: evita la
// ambigüedad entre cuantificadores adyacentes que dispara sonarjs/slow-regex (ReDoS).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

interface TraceFiltersProps {
  isPending: boolean;
  onSearch: (mode: TraceFilterMode, term: string) => void;
  onClear: () => void;
}

export function TraceFilters({ isPending, onSearch, onClear }: TraceFiltersProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<TraceFilterMode>('subject');
  const [term, setTerm] = useState('');
  const [validationKey, setValidationKey] = useState<string | null>(null);

  const trimmedTerm = term.trim();
  const isSearchDisabled = isPending || trimmedTerm.length === 0;

  function handleSearch() {
    if (mode === 'email' && !EMAIL_PATTERN.test(trimmedTerm)) {
      setValidationKey('trace.invalidEmail');
      return;
    }
    setValidationKey(null);
    onSearch(mode, trimmedTerm);
  }

  function handleModeChange(nextMode: TraceFilterMode) {
    setMode(nextMode);
    setValidationKey(null);
  }

  function handleClear() {
    setTerm('');
    setValidationKey(null);
    onClear();
  }

  return (
    <div className="trc-filters">
      <fieldset className="trc-filters-modes">
        <legend className="trc-filters-legend">{t('trace.filterModeLabel')}</legend>
        <label className="trc-filters-mode">
          <input
            type="radio"
            name="trace-mode"
            checked={mode === 'subject'}
            onChange={() => handleModeChange('subject')}
          />
          {t('trace.filterModeSubject')}
        </label>
        <label className="trc-filters-mode">
          <input
            type="radio"
            name="trace-mode"
            checked={mode === 'email'}
            onChange={() => handleModeChange('email')}
          />
          {t('trace.filterModeEmail')}
        </label>
      </fieldset>

      <div className="trc-filters-row">
        <input
          className="trc-filters-input"
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter' && !isSearchDisabled) handleSearch(); }}
          placeholder={mode === 'email'
            ? t('trace.filterPlaceholderEmail')
            : t('trace.filterPlaceholderSubject')}
        />
        <button
          className="trc-btn-search"
          type="button"
          onClick={handleSearch}
          disabled={isSearchDisabled}
        >
          <i className={`fas fa-search${isPending ? ' fa-spin' : ''}`} aria-hidden="true" />
          {isPending ? t('trace.searching') : t('trace.search')}
        </button>
        <button className="trc-btn-clear" type="button" onClick={handleClear}>
          {t('trace.clear')}
        </button>
      </div>

      {validationKey && (
        <p className="trc-validation">
          <i className="fas fa-exclamation-circle" aria-hidden="true" /> {t(validationKey)}
        </p>
      )}
    </div>
  );
}
